/**
 * Feedback Page
 *
 * Simple page for users to:
 * - Report issues with verification results
 * - Request re-verification (e.g., source content changed)
 * - Provide general feedback
 *
 * Primary correction mechanism is the human validation feature on results.
 */

import { useState } from 'react'
import { feedbackApi } from '../services/api'

type FeedbackType = 'issue' | 'reverify' | 'general'

const FEEDBACK_TYPES: FeedbackType[] = ['issue', 'reverify', 'general']

const isFeedbackType = (value: string): value is FeedbackType => {
  return FEEDBACK_TYPES.includes(value as FeedbackType)
}

export const Feedback = () => {
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('issue')
  const [formData, setFormData] = useState({
    resultId: '',
    email: '',
    message: '',
  })
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (feedbackType !== 'general' && !formData.resultId.trim()) {
      newErrors.resultId = 'Please provide the result ID'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Please describe your feedback'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    setSubmitting(true)
    setSubmitError(null)

    try {
      await feedbackApi.submitFeedback({
        feedback_type: feedbackType,
        result_id: formData.resultId || undefined,
        email: formData.email,
        message: formData.message,
      })
      setSubmitted(true)
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit feedback')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 1rem' }}>
      <h1 style={{ color: '#2c3e50', marginBottom: '1.5rem' }}>Feedback</h1>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#34495e' }}>Correcting Results</h2>
        <p style={{ lineHeight: 1.7, color: '#555' }}>
          The best way to correct a verification result is to use the{' '}
          <strong>human validation feature</strong> directly on the result page. This lets you
          confirm or override the AI assessment with your own judgement and notes.
        </p>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: '#34495e' }}>Other Feedback</h2>
        <p style={{ lineHeight: 1.7, color: '#555', marginBottom: '1rem' }}>
          Use this form to report issues, request re-verification, or send general feedback.
        </p>

        {submitted ? (
          <div
            style={{
              background: '#e8f5e9',
              padding: '1.5rem',
              borderRadius: '8px',
              border: '1px solid #81c784',
            }}
          >
            <h3 style={{ color: '#2e7d32', marginTop: 0 }}>Thanks for your feedback</h3>
            <p style={{ color: '#555', margin: 0 }}>
              We&apos;ve received your feedback and will get back to you at the email address you
              provided.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                What type of feedback?
              </label>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                {[
                  { value: 'issue', label: 'Report an issue' },
                  { value: 'reverify', label: 'Request re-verification' },
                  { value: 'general', label: 'General feedback' },
                ].map((option) => (
                  <label
                    key={option.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="feedbackType"
                      value={option.value}
                      checked={feedbackType === option.value}
                      onChange={(e) => {
                        if (isFeedbackType(e.target.value)) {
                          setFeedbackType(e.target.value)
                        }
                      }}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>

            {feedbackType !== 'general' && (
              <div style={{ marginBottom: '1rem' }}>
                <label
                  htmlFor="resultId"
                  style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}
                >
                  Result ID *
                </label>
                <input
                  type="text"
                  id="resultId"
                  value={formData.resultId}
                  onChange={(e) => {
                    setFormData({ ...formData, resultId: e.target.value })
                    if (errors.resultId) setErrors({ ...errors, resultId: '' })
                  }}
                  placeholder="e.g., abc123"
                  aria-invalid={!!errors.resultId}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '4px',
                    border: errors.resultId ? '1px solid #e74c3c' : '1px solid #ddd',
                    fontSize: '1rem',
                  }}
                />
                {errors.resultId && (
                  <p style={{ color: '#e74c3c', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                    {errors.resultId}
                  </p>
                )}
              </div>
            )}

            <div style={{ marginBottom: '1rem' }}>
              <label
                htmlFor="email"
                style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}
              >
                Your Email *
              </label>
              <input
                type="email"
                id="email"
                value={formData.email}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value })
                  if (errors.email) setErrors({ ...errors, email: '' })
                }}
                placeholder="your@email.com"
                aria-invalid={!!errors.email}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: errors.email ? '1px solid #e74c3c' : '1px solid #ddd',
                  fontSize: '1rem',
                }}
              />
              {errors.email && (
                <p style={{ color: '#e74c3c', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                  {errors.email}
                </p>
              )}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label
                htmlFor="message"
                style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}
              >
                {feedbackType === 'issue' && 'Describe the issue *'}
                {feedbackType === 'reverify' && 'Why should we re-verify? *'}
                {feedbackType === 'general' && 'Your feedback *'}
              </label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => {
                  setFormData({ ...formData, message: e.target.value })
                  if (errors.message) setErrors({ ...errors, message: '' })
                }}
                placeholder={
                  feedbackType === 'reverify'
                    ? 'e.g., The source content has been updated since verification...'
                    : ''
                }
                rows={4}
                aria-invalid={!!errors.message}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '4px',
                  border: errors.message ? '1px solid #e74c3c' : '1px solid #ddd',
                  fontSize: '1rem',
                  resize: 'vertical',
                }}
              />
              {errors.message && (
                <p style={{ color: '#e74c3c', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                  {errors.message}
                </p>
              )}
            </div>

            {submitError && (
              <div
                style={{
                  background: '#ffebee',
                  color: '#c62828',
                  padding: '0.75rem 1rem',
                  borderRadius: '4px',
                  marginBottom: '1rem',
                  border: '1px solid #ef9a9a',
                }}
              >
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                background: submitting ? '#95a5a6' : '#3498db',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '4px',
                fontSize: '1rem',
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </form>
        )}
      </section>
    </div>
  )
}
